// Started dependents of tasks about to be reopened (owner answer 7b, C4): dependents are read from the board
// (`depends_on`), and those already past `backlog|in_analysis|todo|cancelled` are listed in the confirmation. They are
// warned about, never paused. Shared by the Kanban reopen and the closure undo of the codebase analysis panel.
import type { Board, TaskCard, TaskStatus } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getBoard } from "../../api/endpoints.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { statusView } from "../../lib/status.ts";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";

const NOT_STARTED: ReadonlySet<string> = new Set<TaskStatus>(["backlog", "in_analysis", "todo", "cancelled"]);

export const boardTasks = (board: Board): TaskCard[] => [...board.columns.flatMap((c) => c.tasks), ...board.side_lane.flatMap((s) => s.tasks)];

/** Tasks depending on any of `ids` that already started; the reopened tasks themselves are not listed. */
export function startedDependents(tasks: readonly TaskCard[], ids: readonly string[]): TaskCard[] {
  const reopening = new Set(ids);
  return tasks.filter((x) => !reopening.has(x.id) && x.depends_on.some((d) => reopening.has(d)) && !NOT_STARTED.has(x.status));
}

export function StartedDependentsList({ tasks }: { tasks: readonly TaskCard[] }) {
  const { t } = useI18n();
  if (tasks.length === 0) return null;
  return (
    <div className="stack" data-testid="started-dependents">
      <p className="tone-warning">{t("onboarding.startedDependents")}</p>
      <ul className="plain-list">
        {tasks.map((d) => (
          <li key={d.id}>
            <span className="mono">#{d.number}</span> <span className="user-content">{d.title}</span> <span className="subtle">({t(statusView(d.status).label)})</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Loads the project's board, then lists the started dependents of `ids` (used where the board is not at hand). */
export function BoardStartedDependents({ projectId, ids }: { projectId: string; ids: readonly string[] }) {
  const { t } = useI18n();
  const board = useAsync((signal) => getBoard(projectId, signal), [projectId]);
  if (board.loading) return <LoadingBlock label={t("common.loading")} lines={1} />;
  if (board.error || !board.data) return <ErrorBanner error={board.error} onRetry={board.reload} />;
  return <StartedDependentsList tasks={startedDependents(boardTasks(board.data), ids)} />;
}
